import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useBusinessUnitStore } from "@/stores/businessUnit";
import { usesGlobalFacilitySelection } from "@/lib/facilityScope";
import { useChatStore } from "../stores/chat";
import type { BusinessUnit } from "@/types";

/**
 * Resolves the facility the AI is actually scoped to.
 *
 * Pages without a global facility selector do not inherit a remembered scope.
 * Only a deliberate reference selected during this page visit may scope their
 * conversation. Editing uses the saved facility on the route. Other pages
 * retain conversation/header scope.
 * An explicit null clears AI scoping without changing the header selection.
 */
export function useEffectiveFacility(): {
  facility: BusinessUnit | undefined;
  isGlobal: boolean;
} {
  const { pathname, key: contextKey, state } = useLocation();
  const selectedBusinessUnit = useChatStore((s) => s.selectedBusinessUnit);
  const selectedContextKey = useChatStore(
    (s) => s.selectedBusinessUnitContextKey,
  );
  const businessUnits = useBusinessUnitStore((s) => s.businessUnits);
  const currentBusinessUnit = useBusinessUnitStore(
    (s) => s.currentBusinessUnit,
  );

  const globalFacility = useMemo(() => {
    if (!currentBusinessUnit) return undefined;
    /* IDs arrive as numbers from the API but are typed as strings. */
    return businessUnits.find(
      (bu) => String(bu.id) === String(currentBusinessUnit),
    );
  }, [businessUnits, currentBusinessUnit]);

  if (selectedBusinessUnit === null) {
    return { facility: undefined, isGlobal: false };
  }
  const editRoute = matchPath("/organisation/facilities/:id/edit", pathname);
  const isClone = !!(state as { isClone?: boolean } | null)?.isClone;
  const explicitlySelectedHere =
    selectedBusinessUnit && selectedContextKey === contextKey;
  if (editRoute && !isClone) {
    return {
      facility: explicitlySelectedHere
        ? selectedBusinessUnit
        : businessUnits.find((bu) => String(bu.id) === editRoute.params.id),
      isGlobal: false,
    };
  }
  if (!usesGlobalFacilitySelection(pathname)) {
    return {
      facility: explicitlySelectedHere ? selectedBusinessUnit : undefined,
      isGlobal: false,
    };
  }
  if (selectedBusinessUnit) {
    return { facility: selectedBusinessUnit, isGlobal: false };
  }
  return { facility: globalFacility, isGlobal: !!globalFacility };
}
